import { useRef, useState } from "react";
import { PageHero } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { ROUTES } from "../lib/routes";
import type { Visit } from "../lib/visits";
import { VISITS, VISIT_PHOTO_COUNT, visitsByHonour } from "../lib/visits";

const ALL = "All";
const TIERS = visitsByHonour();

function VisitCard({ v }: { v: Visit }) {
  const [lead, ...rest] = v.photos;
  return (
    <article className="visit-card reveal">
      <Figure
        media={lead}
        fallbackLabel={v.name.toUpperCase()}
        aspect="3/2"
        sizes="(max-width: 700px) 100vw, (max-width: 1180px) 46vw, 31vw"
        className="visit-lead"
      />
      <div className="visit-body">
        {v.honour && <div className="visit-honour">{v.honour}</div>}
        <h3>{v.name}</h3>
        <div className="visit-affil">{v.affiliation}</div>
        {v.blurb && <p>{v.blurb}</p>}
        {rest.length > 0 && (
          <div className="visit-strip">
            {rest.map((p) => (
              <Figure key={p.url} media={p} fallbackLabel={v.name} aspect="3/2" sizes="140px" />
            ))}
          </div>
        )}
        {v.date && <div className="visit-date">Visited {v.date}</div>}
      </div>
    </article>
  );
}

export default function Visitors() {
  const [active, setActive] = useState<string>(ALL);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const tabs = [
    { key: ALL, label: "All", count: VISITS.length },
    ...TIERS.map((t) => ({ key: t.label, label: t.label, count: t.visits.length })),
  ];

  const shown = active === ALL ? TIERS : TIERS.filter((t) => t.label === active);

  // Roving-focus keyboard nav across the tab strip (WAI-ARIA tabs pattern),
  // matching the Alumni and Mandatory Disclosures filters.
  function onTabKeyDown(e: React.KeyboardEvent, i: number) {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!delta && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : (i + delta + tabs.length) % tabs.length;
    setActive(tabs[next].key);
    tabRefs.current[next]?.focus();
  }

  return (
    <>
      <PageHero
        variant="hero-editorial"
        eyebrow="About · Distinguished visitors"
        title="Who has stood on this campus."
        lead={`Scientists, academicians and public figures the institute has received — ${VISITS.length} guests and ${VISIT_PHOTO_COUNT} photographs from its own record, from a President of India to the teams that build the country's satellites.`}
        crumbs={[{ label: "About", to: ROUTES.about }, { label: "Distinguished visitors" }]}
        heroMedia={VISITS[0].photos[0]}
        image="DISTINGUISHED VISITORS"
      />

      <section className="section">
        <div className="container">
          <div className="filter-tabs" role="tablist" aria-label="Filter visitors by honour">
            {tabs.map((t, i) => (
              <button
                key={t.key}
                ref={(el) => { tabRefs.current[i] = el; }}
                role="tab"
                type="button"
                id={`vs-tab-${i}`}
                aria-selected={active === t.key}
                aria-controls="vs-panel"
                tabIndex={active === t.key ? 0 : -1}
                className={"filter-tab" + (active === t.key ? " is-active" : "")}
                onClick={() => setActive(t.key)}
                onKeyDown={(e) => onTabKeyDown(e, i)}
              >
                {t.label}
                <span className="filter-tab-count">{t.count}</span>
              </button>
            ))}
          </div>

          <div id="vs-panel" role="tabpanel" aria-labelledby={`vs-tab-${tabs.findIndex((t) => t.key === active)}`}>
            {shown.map((tier) => (
              <div key={tier.label}>
                <div className="visit-tier">
                  <h2>{tier.label}</h2>
                  <span>{tier.visits.length}</span>
                </div>
                <div className="visit-grid">
                  {tier.visits.map((v) => (
                    <VisitCard key={v.slug} v={v} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
